import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { FormDrawer } from '../../../../../components/ui';
import OptionModal from '../../../../../components/Modals/OptionModal.tsx';
import EntitySettingsModal from '../../../../../components/Leads/LeadForm/EntitySettingsModal.tsx';
import FieldSettings from '../../../../../components/Leads/LeadForm/FieldSettings.tsx';
import { OPTION_FIELD_TYPES } from '../../constants/formStudioFieldTypes';
import { FORM_STUDIO_COPY } from '../../constants/formStudioConfig';
import {
  buildFieldDrawerSavePayload,
  isAliasNameLocked,
  isFieldNameLocked,
  isFieldTypeLocked,
  isSystemBuilderField,
  isSystemFieldAliasOnlyMode,
  isStatusField,
  isSubStatusField,
} from '../../utils/formStudioFormUtils';
import {
  UI_FIELD_TYPE_STATUS,
  UI_FIELD_TYPE_SUB_STATUS,
  UI_FIELD_TYPES,
  normalizeFieldForBackend,
  toUiFieldType,
} from '../../utils/fieldTypeAdapter';
import { addGroupOption, resolveGroupTitle } from '../../utils/fieldGroupUtils';
import {
  mergeOptionColors,
  normalizeFieldOptions,
  shouldShowFieldOptionsPreview,
} from '../../utils/fieldOptionsUtils';
import FieldGroupSettings from './FieldGroupSettings';
import FieldHotRecordSettings from './FieldHotRecordSettings';
import FieldOptionsPreview from './FieldOptionsPreview';
import FieldStatusOptionsPanel from './FieldStatusOptionsPanel';
import FieldSuggestionSettings from './FieldSuggestionSettings';
import StatusOptionSubstatusPanel from './StatusOptionSubstatusPanel';

function modifyEntityFormFieldsArray(list) {
  if (!list) return [];
  return list.map((item) => ({
    name: item?.name || item?.key_name,
    label: item?.alias_name || item?.key_name,
  }));
}

function getDrawerTitle(field) {
  if (isSystemFieldAliasOnlyMode(field)) return 'Edit system field';
  if (isSubStatusField(field)) return 'Edit sub-status field';
  if (isStatusField(field)) return 'Edit status field';
  return 'Edit field';
}

const BASE_FIELD_TOGGLES = Object.freeze([
  ['is_required', 'Required'],
  ['is_primary', 'Primary'],
  ['is_encrypted', 'Encrypted'],
]);

const ADVANCED_FIELD_TOGGLES = Object.freeze([
  ['isFilter', 'Filterable'],
  ['isSuggestion', 'Suggestion'],
  ['is_hot_lead', 'Hot record'],
  ['isGroup', 'Group field'],
]);

export default function FormFieldDrawer({
  open,
  fieldIndex,
  field,
  allFields,
  fieldErrors,
  workspaceId,
  groupOptions = [],
  onUpdateGroups,
  onManageGroups,
  onClose,
  onSave,
}) {
  const statusFieldCopy = FORM_STUDIO_COPY.statusField;
  const [draft, setDraft] = useState(field || {});
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [settingData, setSettingData] = useState({ permissions: [] });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (field) {
      setDraft(field);
      setSettingData(field.settings || { permissions: [] });
      setIsDirty(false);
    }
  }, [field, open]);

  const isSystemField = isSystemBuilderField(draft);
  const uiFieldType = draft.ui_key_type ?? toUiFieldType(draft);
  const isStatusUiType = uiFieldType === UI_FIELD_TYPE_STATUS;
  const isSubStatusUiType = uiFieldType === UI_FIELD_TYPE_SUB_STATUS;
  const isPlainOptionType =
    OPTION_FIELD_TYPES.includes(uiFieldType) && !isStatusUiType && !isSubStatusUiType;
  const showOptionsPreview = shouldShowFieldOptionsPreview(draft);
  const keyNameReady = Boolean(String(draft.key_name || '').trim());
  const statusFields = useMemo(
    () => (allFields || []).filter(
      (item, index) => index !== fieldIndex && isStatusField(item)
    ),
    [allFields, fieldIndex]
  );
  const selectedParentStatus = useMemo(
    () => statusFields.find(
      (item) => item.key_name === draft.status_parent_key
    ) || null,
    [draft.status_parent_key, statusFields]
  );

  const updateDraft = (patch) => {
    setIsDirty(true);
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const handleSettingDataChange = (next) => {
    setIsDirty(true);
    setSettingData(next);
  };

  const handleTypeChange = (nextUiType) => {
    setIsDirty(true);
    let updated = {
      ...draft,
      ui_key_type: nextUiType,
    };

    if (nextUiType === UI_FIELD_TYPE_STATUS) {
      updated = {
        ...updated,
        key_type: 'DROPDOWN',
        statusOption: 'is_status',
        status_parent_key: '',
        key_options: draft.key_options || [],
      };
      delete updated.substatuses_by_parent_option;
      setShowOptionModal(true);
    } else if (nextUiType === UI_FIELD_TYPE_SUB_STATUS) {
      updated = {
        ...updated,
        key_type: 'DROPDOWN',
        statusOption: 'is_sub_status',
        status_parent_key: '',
        key_options: [],
        option_colors: {},
        substatuses_by_parent_option: {},
      };
    } else if (OPTION_FIELD_TYPES.includes(nextUiType)) {
      updated = {
        ...updated,
        key_type: nextUiType,
        statusOption: '',
        status_parent_key: '',
      };
      delete updated.substatuses_by_parent_option;
      setShowOptionModal(true);
    } else {
      updated = normalizeFieldForBackend({
        ...updated,
        ui_key_type: nextUiType,
      });
      delete updated.key_options;
      delete updated.option_colors;
      delete updated.substatuses_by_parent_option;
    }

    if (nextUiType === 'ENTITY') {
      setShowEntityModal(true);
    }

    setDraft(updated);
  };

  const buildApplyPayload = () =>
    buildFieldDrawerSavePayload(
      field,
      {
        ...draft,
        ui_key_type: uiFieldType,
      },
      settingData
    );

  const handleSave = () => {
    onSave(fieldIndex, buildApplyPayload());
    onClose();
  };

  const handleParentStatusChange = (parentKey) => {
    const parent = statusFields.find((item) => item.key_name === parentKey);
    const parentOptions = normalizeFieldOptions(parent?.key_options);
    updateDraft({
      status_parent_key: parentKey,
      key_options: [],
      option_colors: {},
      substatuses_by_parent_option: Object.fromEntries(
        parentOptions.map((optionName) => [optionName, []])
      ),
    });
  };

  const handleOptionSubmit = (options, info) => {
    updateDraft({
      key_options: options,
      option_colors: mergeOptionColors(options, info, draft.option_colors),
      key_type: info?.attributeType || draft.key_type || 'DROPDOWN',
    });
    setShowOptionModal(false);
  };

  const handleAttributeChange = (updatedAttr) => {
    setIsDirty(true);
    setDraft(updatedAttr);
  };

  const handleFieldToggle = (key, checked) => {
    const patch = { [key]: checked };

    if (key === 'is_hot_lead' && checked && OPTION_FIELD_TYPES.includes(draft.key_type)) {
      const options = (draft.key_options || []).filter(Boolean);
      if (options.length && !String(draft.hot_lead_criteria || '').trim()) {
        patch.hot_lead_criteria = options.join(', ');
      }
    }

    if (key === 'isGroup' && !checked) {
      patch.group_title = '';
      patch.groupId = '';
    }

    if (key === 'isSuggestion' && !checked) {
      patch.suggestionMsg = '';
    }

    updateDraft(patch);
  };

  const handleAddGroup = (title) => {
    const result = addGroupOption(groupOptions, title);
    if (!result.ok) {
      return { error: result.error };
    }

    setIsDirty(true);
    onUpdateGroups?.(result.groupOptions);
    setDraft((prev) => ({
      ...prev,
      groupId: result.newGroup.id,
      group_title: result.newGroup.title,
    }));
    return { ok: true };
  };

  const handleSelectGroup = (groupIdValue) => {
    const selectedTitle = resolveGroupTitle(groupOptions, groupIdValue);
    updateDraft({
      groupId: groupIdValue,
      group_title: selectedTitle,
    });
  };

  const handleClearGroupAssignment = () => {
    updateDraft({
      groupId: '',
      group_title: '',
    });
  };

  const fieldToggles = [...BASE_FIELD_TOGGLES, ...ADVANCED_FIELD_TOGGLES];

  const optionModalType = isStatusUiType ? 'DROPDOWN' : uiFieldType;

  if (!open) return null;

  return (
    <>
      <FormDrawer
        open={open}
        onClose={onClose}
        title={getDrawerTitle(draft)}
        subtitle={draft.alias_name || draft.key_name || 'Configure field properties'}
        submitLabel="Apply"
        onSubmit={handleSave}
        confirmBeforeClose={isDirty}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isSystemField ? (
            <Typography variant="body2" color="text.secondary">
              System field — customize the display alias. Field name, type and advanced settings
              cannot be changed.
            </Typography>
          ) : null}

          <TextField
            label="Field name"
            size="small"
            fullWidth
            required
            disabled={isFieldNameLocked(draft)}
            value={draft.key_name === 'lead_id' ? 'record_id' : draft.key_name || ''}
            onChange={(event) =>
              updateDraft({ key_name: event.target.value.replace(/\s/g, '_') })
            }
            error={Boolean(fieldErrors?.key_name)}
            helperText={fieldErrors?.key_name}
            sx={{
              '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                top: '2px',
              },
            }}
          />
          <TextField
            label="Alias name"
            size="small"
            fullWidth
            required
            disabled={isAliasNameLocked(draft)}
            value={draft.alias_name || ''}
            onChange={(event) => updateDraft({ alias_name: event.target.value })}
            error={Boolean(fieldErrors?.alias_name)}
            helperText={fieldErrors?.alias_name}
          />
          <TextField
            select
            label="Field type"
            size="small"
            fullWidth
            disabled={isFieldTypeLocked(draft)}
            value={uiFieldType || 'STRING'}
            onChange={(event) => handleTypeChange(event.target.value)}
            error={Boolean(fieldErrors?.key_type)}
            helperText={fieldErrors?.key_type}
          >
            {UI_FIELD_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>

          {!isSystemField && isSubStatusUiType ? (
            <>
              <TextField
                select
                label={statusFieldCopy.parentFieldLabel}
                size="small"
                fullWidth
                required
                value={draft.status_parent_key || ''}
                onChange={(event) => handleParentStatusChange(event.target.value)}
                error={Boolean(fieldErrors?.status_parent_key)}
                helperText={
                  fieldErrors?.status_parent_key
                  || (!statusFields.length ? statusFieldCopy.noParentStatusFields : '')
                }
              >
                <MenuItem value="" disabled>
                  {statusFieldCopy.selectParentStatus}
                </MenuItem>
                {statusFields.map((statusField) => (
                  <MenuItem
                    key={`${statusField.item_id}-${statusField.key_name}`}
                    value={statusField.key_name}
                    disabled={!String(statusField.key_name || '').trim()}
                  >
                    {statusField.alias_name || statusField.key_name || 'Untitled status field'}
                  </MenuItem>
                ))}
              </TextField>
              {selectedParentStatus
                && normalizeFieldOptions(selectedParentStatus.key_options).length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {statusFieldCopy.noParentStatusOptions}
                </Typography>
              ) : null}
              <StatusOptionSubstatusPanel
                parentField={selectedParentStatus}
                subStatusField={draft}
                onChange={(nextDraft) => updateDraft(nextDraft)}
              />
              {fieldErrors?.key_options ? (
                <Typography variant="caption" color="error">
                  {fieldErrors.key_options}
                </Typography>
              ) : null}
            </>
          ) : null}

          {!isSystemField && isStatusUiType ? (
            <>
              <FieldStatusOptionsPanel
                keyName={draft.key_name}
                keyOptions={draft.key_options}
                optionColors={draft.option_colors}
                disabled={!keyNameReady}
                onEditOptions={() => setShowOptionModal(true)}
              />
            </>
          ) : null}

          {!isSystemField && isPlainOptionType ? (
            showOptionsPreview ? (
              <FieldOptionsPreview
                keyOptions={draft.key_options}
                optionColors={draft.option_colors}
                onEdit={() => setShowOptionModal(true)}
              />
            ) : null
          ) : null}

          {!isSystemField && draft.key_type === 'TEXTAREA' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField
                label="Min characters"
                size="small"
                type="number"
                value={draft.minLength ?? ''}
                onChange={(event) => updateDraft({ minLength: event.target.value })}
                error={Boolean(fieldErrors?.minLength)}
                helperText={fieldErrors?.minLength}
              />
              <TextField
                label="Max characters"
                size="small"
                type="number"
                value={draft.maxLength ?? ''}
                onChange={(event) => updateDraft({ maxLength: event.target.value })}
                error={Boolean(fieldErrors?.maxLength)}
                helperText={fieldErrors?.maxLength}
              />
            </Box>
          ) : null}

          {!isSystemField && draft.key_type === 'ENTITY' ? (
            <Typography
              component="button"
              type="button"
              onClick={() => setShowEntityModal(true)}
              sx={{
                textAlign: 'left',
                color: 'primary.main',
                cursor: 'pointer',
                border: 0,
                bgcolor: 'transparent',
              }}
            >
              Configure entity mapping
            </Typography>
          ) : null}

          {!isSystemField ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1,
              }}
            >
              {fieldToggles.map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Switch
                      checked={Boolean(draft[key])}
                      onChange={(event) => handleFieldToggle(key, event.target.checked)}
                    />
                  }
                  label={label}
                />
              ))}
            </Box>
          ) : null}

          {!isSystemField && draft.isGroup ? (
            <FieldGroupSettings
              groupOptions={groupOptions}
              groupTitle={draft.group_title || ''}
              groupId={draft.groupId ?? ''}
              fieldError={fieldErrors?.group_title}
              onSelectGroup={handleSelectGroup}
              onAddGroup={handleAddGroup}
              onClearAssignment={handleClearGroupAssignment}
              onManageGroups={onManageGroups}
            />
          ) : null}

          {!isSystemField && draft.isSuggestion ? (
            <FieldSuggestionSettings
              value={draft.suggestionMsg}
              onChange={(value) => updateDraft({ suggestionMsg: value })}
            />
          ) : null}

          {!isSystemField && draft.is_hot_lead ? (
            <FieldHotRecordSettings
              value={draft.hot_lead_criteria}
              error={fieldErrors?.hot_lead_criteria}
              onChange={(value) => updateDraft({ hot_lead_criteria: value })}
            />
          ) : null}

          {!isSystemField ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Profile permissions
              </Typography>
              <FieldSettings
                settingData={settingData}
                setSettingData={handleSettingDataChange}
                campaignId={workspaceId}
              />
            </Box>
          ) : null}
        </Box>
      </FormDrawer>

      <OptionModal
        showOptionModal={showOptionModal}
        onCancel={() => setShowOptionModal(false)}
        onSubmit={handleOptionSubmit}
        options={draft.key_options || []}
        columnInfo={{
          attributeType: optionModalType,
          attribute: draft,
          attrIndex: fieldIndex,
        }}
      />

      <EntitySettingsModal
        open={showEntityModal}
        onClose={() => setShowEntityModal(false)}
        onSave={() => setShowEntityModal(false)}
        attribute={draft}
        index={fieldIndex}
        errors={{}}
        leadsFormItems={allFields}
        handleAttributeChange={(updatedAttr) => handleAttributeChange(updatedAttr)}
        handleAddFieldMapping={() => {
          handleAttributeChange({
            ...draft,
            query_parameter: [
              ...(draft.query_parameter || []),
              { param: '', matched_with: '' },
            ],
          });
        }}
        modifyEntityFormFieldsArray={modifyEntityFormFieldsArray}
      />
    </>
  );
}
